/**
 * /admin/companies — System Admin Company list (Story 74).
 *
 * Binding contract: `docs/ia/admin-companies.md`.
 *
 * Archetype: header + stat-strip + filter-banded table panel. The B2B
 * operations surface — the SME's editorial intent is "client ledger of a
 * chartered firm: formal, named, with quota and subscription metadata
 * sitting like account-numbers in Space Mono." The stat strip provides the
 * lens for the table beneath it; the filter band reads as the table's
 * control deck.
 *
 * 15-FG/38 restores the stat strip + filter band + "+ New Company" CTA after
 * the audit (29-ia-baseline.md § 6) flagged them as missing. Column-set
 * divergence from the contract (drop Quota — modal-only; drop Members —
 * no drill-in surface; KEEP Tests Assigned + Subscription as the bimassess
 * operational signals) is ratified by ADR-0010.
 *
 * Minimum widgets (contract § Minimum):
 *   1. Page header: h1 "Companies" + editorial sub-label.
 *   2. Top-right primary CTA "+ New Company" → /admin/companies/new.
 *   3. Stat strip with 3 metric cards: Companies · Active · Total Attempts.
 *   4. Filter band: search · status dropdown · clear button.
 *   5. Table with 7 columns per ADR-0010: Company · Contact · Tests Assigned ·
 *      Attempts · Subscription · Status · Actions. Columns 1–6 sortable.
 *   6. Status renders as <Badge>.
 *   7. Loading state row (em-dash); empty state with repeated CTA inside the
 *      table chrome.
 *
 * Substrate (15-FG/30):
 *   - <AdminLayout> wraps <Shell mode='admin' densityMode='workspace'>.
 *   - Primitives only from `@/components/ui` (Heading / Eyebrow / Button /
 *     Card / Badge / Input / Link).
 *   - design.md tokens only — no bg-bg-2 / bg-surface / rounded-2xl /
 *     chromatic borders / second-accent fills.
 *
 * v1 data wiring carve-out:
 *   - Tests Assigned: aggregate endpoint not wired — em-dash placeholder
 *     (same convention as `/admin/tests` Attempts column in 15-FG/36).
 *   - Subscription: `subscription_tier` field not yet on AdminCompanyListItem —
 *     em-dash placeholder; column chrome locked in by ADR-0010.
 *   - Search + status filters operate client-side on the loaded page until
 *     the API supports query params (same pattern as `/admin/attempts`'s
 *     search filter).
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — all CTAs use <Link>.
 *   - Inv 15 (atoms from barrel) — Heading / Button / Card / Badge / Input /
 *     Link only.
 */

import {
  useEffect,
  useState,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  adminApi,
  type AdminCompanyListItem,
} from '../../../state/admin/api.ts';
import { AdminLayout } from '../layout.tsx';
import { Badge, Button, Card, Heading, Input, Link } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';

// ─── Formatting helpers ───────────────────────────────────────────────────────

/** Em-dash — the loading-state and "data-not-yet-wired" placeholder.
 *  Never "Loading…" — matches the dashboard / admin-tests discipline. */
const EM_DASH = '—';

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

function statusVariant(status: AdminCompanyListItem['status']): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ─── Column spec ──────────────────────────────────────────────────────────────

/** Binding column spec per `docs/ia/admin-companies.md` + ADR-0010. */
interface ColumnSpec {
  id:
    | 'company'
    | 'contact'
    | 'tests_assigned'
    | 'attempts'
    | 'status'
    | 'actions';
  label: string;
  /** Columns 1–5 are sortable; Actions is not. */
  sortable: boolean;
  /** Numeric columns right-align + Space Mono per contract Polish 6. */
  numeric?: boolean;
}

const COLUMNS: readonly ColumnSpec[] = [
  { id: 'company', label: 'Company', sortable: true },
  { id: 'contact', label: 'Contact', sortable: true },
  { id: 'tests_assigned', label: 'Tests Assigned', sortable: true, numeric: true },
  { id: 'attempts', label: 'Attempts', sortable: true, numeric: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false },
] as const;

type SortDir = 'asc' | 'desc';
interface SortState {
  column: ColumnSpec['id'];
  direction: SortDir;
}

function sortItems(
  items: AdminCompanyListItem[],
  sort: SortState | null,
): AdminCompanyListItem[] {
  if (!sort) return items;
  const dir = sort.direction === 'asc' ? 1 : -1;
  const sorted = [...items];
  sorted.sort((a, b) => {
    switch (sort.column) {
      case 'company':
        return a.name.localeCompare(b.name) * dir;
      case 'contact':
        return a.contact_email.localeCompare(b.contact_email) * dir;
      case 'tests_assigned':
        return (a.tests_assigned_count - b.tests_assigned_count) * dir;
      case 'attempts':
        return (a.recent_attempt_count_30d - b.recent_attempt_count_30d) * dir;
      case 'status':
        return a.status.localeCompare(b.status) * dir;
      default:
        return 0;
    }
  });
  return sorted;
}

// ─── Composition helpers — co-located ─────────────────────────────────────────

interface StatCardProps {
  label: string;
  /** Pre-formatted value (rendered in Space Mono caller-side). */
  value: ReactNode;
}

/**
 * Stat card — pure type-on-card per contract polish 7. No emoji, no
 * chromatic top border, no trend chevron. 32px Space Mono value; 11px
 * Space Mono uppercase label. Three-card rhythm = wider per slot than the
 * dashboard's four-card variant (contract polish 10).
 */
function StatCard({ label, value }: StatCardProps): ReactElement {
  return (
    <Card variant="default" className="flex flex-col gap-3 p-6">
      <span className="font-mono text-eyebrow uppercase tracking-wide text-muted">
        {label}
      </span>
      <span className="font-mono text-h2 leading-none text-ink">{value}</span>
    </Card>
  );
}

interface SortIndicatorProps {
  active: boolean;
  direction: SortDir;
}

/** Chevron right of a sortable column label. `muted` inactive, `ink` active.
 *  Triangle glyph — no emoji per design.md restraint rule. */
function SortIndicator({ active, direction }: SortIndicatorProps): ReactElement {
  const glyph = active && direction === 'asc' ? '▲' : '▼';
  return (
    <span
      aria-hidden="true"
      className={`ml-1.5 inline-block text-xs leading-none ${
        active ? 'text-ink' : 'text-muted'
      }`}
    >
      {glyph}
    </span>
  );
}

interface ColumnHeaderProps {
  spec: ColumnSpec;
  sort: SortState | null;
  onToggle: (id: ColumnSpec['id']) => void;
}

function ColumnHeader({ spec, sort, onToggle }: ColumnHeaderProps): ReactElement {
  const active = sort?.column === spec.id;
  const ariaSort: 'none' | 'ascending' | 'descending' = active
    ? sort.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const align = spec.numeric ? 'text-right' : 'text-left';
  const justify = spec.numeric ? 'justify-end' : 'justify-start';

  return spec.sortable ? (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`${align} border-b border-line px-4 py-3`}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          onToggle(spec.id);
        }}
        className={`!px-0 !py-0 !font-sans !text-eyebrow !font-medium uppercase tracking-wide text-muted hover:text-accent ${justify} w-full`}
      >
        {spec.label}
        <SortIndicator active={active} direction={sort?.direction ?? 'asc'} />
      </Button>
    </th>
  ) : (
    <th
      scope="col"
      className={`${align} border-b border-line px-4 py-3 font-sans text-eyebrow font-medium uppercase tracking-wide text-muted`}
    >
      {spec.label}
    </th>
  );
}

interface LoadingRowProps {
  columnCount: number;
}

function LoadingRow({ columnCount }: LoadingRowProps): ReactElement {
  return (
    <tr>
      {Array.from({ length: columnCount }, (_, j) => (
        <td key={j} className="px-4 py-3 font-mono text-mono text-muted">
          {EM_DASH}
        </td>
      ))}
    </tr>
  );
}

interface EmptyStateRowProps {
  columnCount: number;
  children: ReactNode;
}

function EmptyStateRow({ columnCount, children }: EmptyStateRowProps): ReactElement {
  return (
    <tr>
      <td colSpan={columnCount} className="px-6 py-12 text-center">
        {children}
      </td>
    </tr>
  );
}

interface CompanyRowProps {
  company: AdminCompanyListItem;
  onAdjustQuota: (company: AdminCompanyListItem) => void;
}

function CompanyRow({ company, onAdjustQuota }: CompanyRowProps): ReactElement {
  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-bg-warm">
      <td className="px-4 py-3 font-sans text-body text-ink">
        <span className="font-medium text-ink">{company.name}</span>
      </td>
      <td className="px-4 py-3 font-sans text-body text-ink-2">
        <span className="block font-sans text-ink-2">{company.contact_name}</span>
        <span className="block font-sans text-small text-muted">
          {company.contact_email}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatInt(company.tests_assigned_count)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatInt(company.recent_attempt_count_30d)}
      </td>
      <td className="px-4 py-3">
        <Badge variant={statusVariant(company.status)}>{company.status}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            onAdjustQuota(company);
          }}
          aria-label={`Adjust quota for ${company.name}`}
        >
          Adjust quota
        </Button>
      </td>
    </tr>
  );
}

// ─── Adjust-quota modal state ─────────────────────────────────────────────────

interface QuotaModalState {
  company: AdminCompanyListItem;
  delta: string;
  reason: string;
  submitting: boolean;
  error: string | null;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminCompaniesListPage(): ReactElement {
  const [items, setItems] = useState<AdminCompanyListItem[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [modal, setModal] = useState<QuotaModalState | null>(null);

  // Filter state — search + status. Search is client-side until the API
  // supports a search param (mirrors /admin/attempts § search filter).
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>(
    '',
  );

  // Sort state.
  const [sort, setSort] = useState<SortState | null>(null);

  const reload = (resetCursor: boolean): void => {
    void (async () => {
      setError(null);
      if (resetCursor) {
        setItems(null);
      }
      const env = await adminApi.listCompanies({
        cursor: resetCursor ? undefined : (cursor ?? undefined),
        limit: 50,
      });
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setItems([]);
        return;
      }
      setItems((prev) =>
        resetCursor || prev === null
          ? env.data.items
          : [...prev, ...env.data.items],
      );
      setCursor(env.data.next_cursor);
    })();
  };

  useEffect(() => {
    reload(true);
    // Mount-only — filter changes apply client-side; pagination only
    // reloads on explicit "Load more" click.
  }, []); // eslint-disable-line -- mount-only effect

  const onToggleSort = (id: ColumnSpec['id']): void => {
    setSort((prev) => {
      if (!prev || prev.column !== id) return { column: id, direction: 'asc' };
      return {
        column: id,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const onClearFilters = (): void => {
    setSearchFilter('');
    setStatusFilter('');
  };

  const onAdjustQuota = (company: AdminCompanyListItem): void => {
    setModal({
      company,
      delta: '',
      reason: '',
      submitting: false,
      error: null,
    });
  };

  const onSubmitAdjust = (): void => {
    if (!modal) return;
    const deltaNum = Number(modal.delta);
    if (!Number.isInteger(deltaNum)) {
      setModal({ ...modal, error: 'delta must be an integer' });
      return;
    }
    if (modal.reason.trim().length === 0) {
      setModal({ ...modal, error: 'reason is required' });
      return;
    }
    void (async () => {
      setModal({ ...modal, submitting: true, error: null });
      const env = await adminApi.adjustQuota(modal.company.id, {
        delta: deltaNum,
        reason: modal.reason.trim(),
      });
      if (!env.ok) {
        if (env.error.code === 'not_implemented_in_01') {
          setModal(null);
          setInfo(
            'Quota adjustment is pending — Module 5 will ship the real implementation in 08-company-workspace/02.',
          );
          return;
        }
        setModal({
          ...modal,
          submitting: false,
          error: `${env.error.code}: ${env.error.message}`,
        });
        return;
      }
      setModal(null);
      setInfo(`Quota adjusted. New used: ${String(env.data.quota_used)}.`);
      reload(true);
    })();
  };

  // ── Derived state ──

  const isLoading = items === null;

  // Stat strip uses the unfiltered page total — gives the admin the
  // "state of our B2B book" lens regardless of the active filter (matches
  // /admin/attempts §summaryRows discipline).
  const allItems = items ?? [];
  const statTotal = allItems.length;
  const statActive = allItems.filter((c) => c.status === 'active').length;
  const statAttempts = allItems.reduce(
    (sum, c) => sum + c.recent_attempt_count_30d,
    0,
  );

  // Filtered + sorted view for the table body.
  const searchNeedle = searchFilter.trim().toLowerCase();
  const filtered = useMemo(() => {
    return allItems.filter((c) => {
      if (statusFilter !== '' && c.status !== statusFilter) return false;
      if (searchNeedle === '') return true;
      return (
        c.name.toLowerCase().includes(searchNeedle) ||
        c.contact_email.toLowerCase().includes(searchNeedle) ||
        c.contact_name.toLowerCase().includes(searchNeedle)
      );
    });
  }, [allItems, statusFilter, searchNeedle]);

  const sorted = sortItems(filtered, sort);
  const isEmpty = items !== null && items.length === 0;
  const filteredEmpty =
    items !== null && items.length > 0 && filtered.length === 0;

  return (
    <AdminLayout title="Companies" eyebrow="Admin · Companies">
      {/* Header CTA cluster — editorial sub-label left, primary CTA right.
          24px top margin establishes the header→stat-strip rhythm per
          contract polish 2. */}
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <p className="font-sans text-small text-muted">
          Manage company access and test assignments
        </p>
        <Link
          to="/admin/companies/new"
          className="inline-flex items-center justify-center rounded-md bg-ink px-5 py-2.5 font-sans text-small font-medium text-bg no-underline hover:bg-ink-2 hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2"
        >
          + New Company
        </Link>
      </div>

      {error ? (
        <p role="alert" className="mt-4 font-sans text-small text-error">
          {error}
        </p>
      ) : null}

      {info ? (
        <p role="status" className="mt-4 font-sans text-small text-ink-2">
          {info}
        </p>
      ) : null}

      {/* Stat strip — 3 metric cards per contract Minimum 3. Region
          landmark labelled "System overview" so the IA-conformance test
          can address it semantically. 20px gap per contract polish 2;
          3-card rhythm wider per slot than dashboard's 4-card variant. */}
      <section
        aria-label="System overview"
        className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3"
      >
        <StatCard
          label="Companies"
          value={isLoading ? EM_DASH : formatInt(statTotal)}
        />
        <StatCard
          label="Active"
          value={isLoading ? EM_DASH : formatInt(statActive)}
        />
        <StatCard
          label="Total Attempts"
          value={isLoading ? EM_DASH : formatInt(statAttempts)}
        />
      </section>

      {/* Table panel — single full-bleed Card containing filter band +
          7-column sortable table. The filter band attaches flush to the
          top of the Card (contract polish 8: "the filter bar and table
          share the Card's chrome"). */}
      <section
        role="region"
        aria-labelledby="admin-companies-table-heading"
        className="mt-6"
      >
        <h2 id="admin-companies-table-heading" className="sr-only">
          Companies table
        </h2>

        <Card variant="default" className="overflow-hidden p-0">
          {/* Filter band — search · status · clear. Region landmark
              `role="search"` so the IA-conformance test can scope queries
              to it without colliding with other inputs on the page. */}
          <section
            role="search"
            aria-label="Filter companies"
            className="border-b border-line bg-bg-warm px-4 py-3"
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="md:col-span-1">
                <label
                  htmlFor="companies-search"
                  className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
                >
                  Search
                </label>
                <Input
                  id="companies-search"
                  type="text"
                  placeholder="Search companies…"
                  value={searchFilter}
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                  }}
                />
              </div>
              <div className="md:col-span-1">
                <label
                  htmlFor="companies-status"
                  className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
                >
                  Status
                </label>
                <select
                  id="companies-status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as '' | 'active' | 'inactive');
                  }}
                  className="w-full rounded-md border border-line-2 bg-card px-3 py-2.5 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex items-end justify-end md:col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          </section>

          {/* Table — 7 columns per ADR-0010. */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-small">
              <thead>
                <tr>
                  {COLUMNS.map((spec) => (
                    <ColumnHeader
                      key={spec.id}
                      spec={spec}
                      sort={sort}
                      onToggle={onToggleSort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <>
                    <LoadingRow columnCount={COLUMNS.length} />
                    <LoadingRow columnCount={COLUMNS.length} />
                    <LoadingRow columnCount={COLUMNS.length} />
                  </>
                ) : isEmpty ? (
                  <EmptyStateRow columnCount={COLUMNS.length}>
                    <Heading level="h2" className="text-h3 text-ink">
                      No companies yet
                    </Heading>
                    <p className="mt-2 font-sans text-small text-muted">
                      Add your first B2B account to get started.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <Link
                        to="/admin/companies/new"
                        className="inline-flex items-center justify-center rounded-md bg-ink px-5 py-2.5 font-sans text-small font-medium text-bg no-underline hover:bg-ink-2 hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2"
                      >
                        + New Company
                      </Link>
                    </div>
                  </EmptyStateRow>
                ) : filteredEmpty ? (
                  <EmptyStateRow columnCount={COLUMNS.length}>
                    <Heading level="h2" className="text-h3 text-ink">
                      No matches
                    </Heading>
                    <p className="mt-2 font-sans text-small text-muted">
                      No companies match the current filters.
                    </p>
                  </EmptyStateRow>
                ) : (
                  sorted.map((c) => (
                    <CompanyRow
                      key={c.id}
                      company={c}
                      onAdjustQuota={onAdjustQuota}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {cursor ? (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                reload(false);
              }}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </section>

      {/* Adjust-quota modal — preserved from the pre-15-FG/38 build.
          Story 77's affordance. Backend stub returns not_implemented_in_01
          which surfaces as a pending-feature info banner. */}
      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="adjust-quota-heading"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        >
          <Card
            variant="default"
            className="mx-auto w-full max-w-md p-6"
          >
            <Heading
              level="h2"
              id="adjust-quota-heading"
              className="text-h3 text-ink"
            >
              Adjust quota — {modal.company.name}
            </Heading>
            <p className="mt-2 font-sans text-small text-muted">
              Current: {modal.company.quota_used} / {modal.company.quota_total}
            </p>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Delta (positive = grant, negative = revoke)
                </span>
                <Input
                  type="number"
                  step="1"
                  value={modal.delta}
                  onChange={(e) => {
                    setModal({ ...modal, delta: e.target.value, error: null });
                  }}
                  disabled={modal.submitting}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Reason (required, recorded in audit log)
                </span>
                <textarea
                  value={modal.reason}
                  onChange={(e) => {
                    setModal({
                      ...modal,
                      reason: e.target.value,
                      error: null,
                    });
                  }}
                  disabled={modal.submitting}
                  rows={3}
                  className="w-full rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
                />
              </label>
            </div>
            {modal.error ? (
              <p role="alert" className="mt-3 font-sans text-small text-error">
                {modal.error}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setModal(null);
                }}
                disabled={modal.submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onSubmitAdjust}
                disabled={modal.submitting}
              >
                {modal.submitting ? 'Submitting…' : 'Adjust'}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </AdminLayout>
  );
}
